import type { LayoutRecord } from "@/shared/domain/layouts";

export interface LayoutOption {
  id: string;
  record: LayoutRecord;
  isDefault: boolean;
}

/** `folder === null` is the ungrouped bucket for layouts with no folder assigned. */
export interface LayoutFolderGroup {
  folder: string | null;
  layouts: LayoutOption[];
}

/** Build the display option for a layout, deriving `isDefault` from the user's default-layout config. */
export function toLayoutOption(
  layout: LayoutRecord,
  defaultLayoutId: string | null,
): LayoutOption {
  return {
    id: layout.layout_id,
    record: layout,
    isDefault: defaultLayoutId === layout.layout_id,
  };
}

/**
 * Group layouts into folders for display. Named folders first (sorted case-insensitively),
 * ungrouped bucket last; layouts within a folder sorted by name. Blank or whitespace-only
 * folder values count as ungrouped, same as `null` (matching `collectFolderNames`). A non-empty
 * `searchQuery` keeps only layouts matching on name or folder name (case-insensitive),
 * dropping empty folders.
 */
export function groupLayoutsIntoFolders(
  layouts: LayoutRecord[],
  defaultLayoutId: string | null,
  searchQuery = "",
): LayoutFolderGroup[] {
  const query = searchQuery.trim().toLowerCase();

  const layoutsByFolder = new Map<string | null, LayoutOption[]>();
  for (const layout of layouts) {
    const folder = layout.folder?.trim() || null;
    const matchesSearch =
      query === "" ||
      layout.name.toLowerCase().includes(query) ||
      (folder ?? "").toLowerCase().includes(query);
    if (!matchesSearch) {
      continue;
    }
    const option = toLayoutOption(layout, defaultLayoutId);
    const options = layoutsByFolder.get(folder);
    if (options) {
      options.push(option);
    } else {
      layoutsByFolder.set(folder, [option]);
    }
  }

  for (const options of layoutsByFolder.values()) {
    options.sort((a, b) => a.record.name.localeCompare(b.record.name));
  }

  const groups: LayoutFolderGroup[] = [...layoutsByFolder.keys()]
    .filter((folder): folder is string => folder !== null)
    .sort((a, b) => a.localeCompare(b))
    .map((folder) => ({ folder, layouts: layoutsByFolder.get(folder) ?? [] }));

  const ungrouped = layoutsByFolder.get(null);
  if (ungrouped) {
    groups.push({ folder: null, layouts: ungrouped });
  }

  return groups;
}

/** Distinct non-empty folder names, sorted, to suggest existing folders when moving/creating a layout. */
export function collectFolderNames(layouts: LayoutRecord[]): string[] {
  const names = new Set<string>();
  for (const layout of layouts) {
    const folder = layout.folder?.trim();
    if (folder) {
      names.add(folder);
    }
  }
  return [...names].sort((a, b) => a.localeCompare(b));
}
